import { useState, useEffect, useCallback, useRef } from "react";
import { Bell, X, Check, Phone, UserPlus, AlertTriangle, RefreshCw, BellRing, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import pushNotificationService from "@/services/pushNotifications";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationBell = ({ variant = "light" }) => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const lastNotificationIds = useRef(new Set());

  // Color variants for different backgrounds - make bell more visible
  const bellColors = variant === "dark" 
    ? "text-white hover:bg-white/20 bg-white/10 border border-white/20 rounded-xl" 
    : "text-[#4a1a4a] hover:bg-[#190019]/10";

  const getAuthHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  // Initialize push notifications
  useEffect(() => {
    const initPush = async () => {
      if (pushNotificationService.isSupported()) {
        const granted = await pushNotificationService.init();
        setPushEnabled(granted);
        if (!granted && Notification.permission === 'default') {
          // Show prompt to enable notifications
          setShowPushPrompt(true);
        }
      }
    };
    initPush();
  }, []);

  // Enable push notifications
  const enablePushNotifications = async () => {
    const granted = await pushNotificationService.init();
    setPushEnabled(granted);
    setShowPushPrompt(false);
    if (granted) {
      // Show test notification
      await pushNotificationService.showGeneral(
        'تم تفعيل الإشعارات!',
        'ستصلك الإشعارات الآن على شاشة القفل'
      );
    }
  };

  const fetchUnreadCount = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/notifications/unread-count`, getAuthHeaders());
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Error fetching unread count:", error);
    }
  }, [token, getAuthHeaders]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/notifications`, getAuthHeaders());
      const newNotifications = response.data;
      
      // Check for new notifications and show push notification
      if (pushEnabled) {
        for (const notification of newNotifications) {
          if (!notification.is_read && !lastNotificationIds.current.has(notification.id)) {
            // Show push notification for new unread notifications
            await pushNotificationService.showGeneral(
              notification.title,
              notification.message,
              notification.lead_id ? '/pipeline' : '/'
            );
          }
        }
      }
      
      // Update tracked notification IDs
      lastNotificationIds.current = new Set(newNotifications.map(n => n.id));
      setNotifications(newNotifications);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  }, [token, getAuthHeaders, pushEnabled]);

  useEffect(() => {
    fetchUnreadCount();
    fetchNotifications();
    // Check for new notifications every 15 seconds (more frequent)
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchNotifications();
    }, 15000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, fetchNotifications]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, getAuthHeaders());
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, getAuthHeaders());
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    if (notification.lead_id) {
      navigate('/pipeline');
    }
    setIsOpen(false);
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'follow_up_today':
        return <Phone className="w-5 h-5 text-emerald-500" />;
      case 'lead_assigned':
        return <UserPlus className="w-5 h-5 text-blue-500" />;
      case 'lead_rotated':
        return <RefreshCw className="w-5 h-5 text-orange-500" />;
      case 'follow_up_expired_rotation':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-[#6b2d6b]" />;
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'الآن';
    if (minutes < 60) return `منذ ${minutes} دقيقة`;
    if (hours < 24) return `منذ ${hours} ساعة`;
    return `منذ ${days} يوم`;
  };

  return (
    <div className="relative">
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative p-2 rounded-xl transition-colors ${bellColors}`}
        data-testid="notification-bell"
      >
        {unreadCount > 0 ? (
          <BellRing className="w-6 h-6 animate-bounce" />
        ) : (
          <Bell className="w-6 h-6" />
        )}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          
          {/* Notifications Panel */}
          <div 
            className="fixed md:absolute left-4 md:left-auto md:right-0 top-16 md:top-full mt-2 w-[calc(100%-2rem)] md:w-96 bg-white rounded-2xl shadow-2xl border border-[#190019]/10 z-50 overflow-hidden max-h-[70vh]"
            dir="rtl"
          >
            {/* Header */}
            <div className="p-4 border-b border-[#190019]/10 flex items-center justify-between bg-gradient-to-r from-[#190019]/5 to-[#6b2d6b]/10">
              <h3 className="font-bold text-[#190019] flex items-center gap-2">
                <Bell className="w-5 h-5 text-[#6b2d6b]" />
                {t('notifications') || 'الإشعارات'}
              </h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="text-xs text-[#6b2d6b] hover:text-[#190019] flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-[#190019]/10"
                  >
                    <Check className="w-4 h-4" />
                    {t('markAllRead') || 'قراءة الكل'}
                  </button>
                )}
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-[#190019]/10 rounded-lg"
                >
                  <X className="w-4 h-4 text-[#4a1a4a]" />
                </button>
              </div>
            </div>

            {/* Push Notification Prompt */}
            {showPushPrompt && (
              <div className="p-4 bg-blue-50 border-b border-blue-100">
                <div className="flex items-start gap-3">
                  <BellRing className="w-6 h-6 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-blue-800">تفعيل الإشعارات</p>
                    <p className="text-xs text-blue-600 mt-1">
                      فعّل الإشعارات لتصلك على شاشة القفل
                    </p>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={enablePushNotifications}
                        className="px-3 py-1.5 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 transition-colors"
                      >
                        تفعيل
                      </button>
                      <button
                        onClick={() => setShowPushPrompt(false)}
                        className="px-3 py-1.5 text-blue-600 text-xs font-medium rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        لاحقاً
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Push Status Indicator */}
            {!showPushPrompt && (
              <div className={`px-4 py-2 text-xs flex items-center gap-2 border-b ${pushEnabled ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-gray-50 text-gray-500 border-gray-100'}`}>
                <div className={`w-2 h-2 rounded-full ${pushEnabled ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                {pushEnabled ? 'الإشعارات مفعّلة على شاشة القفل' : 'الإشعارات غير مفعّلة'}
                {!pushEnabled && Notification.permission !== 'denied' && (
                  <button
                    onClick={enablePushNotifications}
                    className="mr-auto text-blue-500 hover:text-blue-700"
                  >
                    تفعيل
                  </button>
                )}
              </div>
            )}

            {/* Notifications List */}
            <div className="max-h-80 overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-2 border-[#6b2d6b] border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-sm text-[#4a1a4a]">جاري التحميل...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <Bell className="w-12 h-12 text-[#190019]/20 mx-auto mb-3" />
                  <p className="text-[#4a1a4a]">{t('noNotifications') || 'لا توجد إشعارات'}</p>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-4 border-b border-[#190019]/5 cursor-pointer hover:bg-[#fbe4d8]/30 transition-colors ${
                      !notification.is_read ? 'bg-[#fbe4d8]/20 border-r-4 border-r-[#6b2d6b]' : ''
                    }`}
                  >
                    <div className="flex gap-3">
                      <div className="flex-shrink-0 mt-1 p-2 rounded-full bg-[#190019]/5">
                        {getNotificationIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notification.is_read ? 'font-bold text-[#190019]' : 'text-[#4a1a4a]'}`}>
                          {notification.title}
                        </p>
                        <p className="text-xs text-[#6b2d6b] mt-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-xs text-[#4a1a4a]/60 mt-2 flex items-center gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-[#4a1a4a]/30" />
                          {formatTime(notification.created_at)}
                        </p>
                      </div>
                      {!notification.is_read && (
                        <div className="w-3 h-3 bg-[#6b2d6b] rounded-full flex-shrink-0 mt-2 animate-pulse" />
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
